import type { App } from '@grafikr/components';
import header from 'layout/header';

import hasHash from 'utils/loaders/has-hash';
import hasRegionMatch from 'utils/loaders/has-region-match';
import hasUrlParam from 'utils/loaders/has-url-param';
import immediateLoader from 'utils/loaders/immediate';
import intersectionLoader from 'utils/loaders/intersection';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from 'utils/loaders/on-event';

export default (app: App) => {
  app.add({
    header,
    'header-drawer-navigation': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "layout-header-drawer-navigation" */ 'layout/header-drawer-navigation'
        ),
    ],

    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    announcements: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-announcements" */ 'components/announcements'),
    ],
    'announcement-bar': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-announcement-bar" */ 'components/announcement-bar'),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'countdown-timer': [
      immediateLoader,
      () =>
        import(/* webpackChunkName: "component-countdown-timer" */ 'components/countdown-timer'),
    ],
    'cart-count': [
      onEvent,
      () => import(/* webpackChunkName: "component-cart-count" */ 'components/cart-count'),
    ],
    'country-selector': [
      immediateLoader,
      () =>
        import(/* webpackChunkName: "component-country-selector" */ 'components/country-selector'),
    ],
    'cart-drawer': [
      [onElementInteraction, hasUrlParam, ...(theme.pageType !== 'cart' ? [onEvent] : [])],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
    ],
    clickable: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-clickable" */ 'components/clickable'),
    ],
    'color-samples-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-color-samples-drawer" */ 'components/color-samples-drawer'
        ),
    ],
    'powerstep-drawer': [
      onEvent,
      () =>
        import(/* webpackChunkName: "component-powerstep-drawer" */ 'components/powerstep-drawer'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    drawer: [
      [onElementInteraction, hasHash],
      () => import(/* webpackChunkName: "component-drawer" */ 'components/drawer'),
    ],
    'eu-guarantee-modal': [
      [onElementInteraction, hasHash],
      () =>
        import(
          /* webpackChunkName: "component-eu-guarantee-modal" */ 'components/eu-guarantee-modal'
        ),
    ],
    'dixa-trigger': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-dixa-trigger" */ 'components/dixa-trigger'),
    ],
    accordion: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-accordion" */ 'components/accordion'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    'installation-guide': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-installation-guide" */ 'components/installation-guide'
        ),
    ],
    instafeed: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-instafeed" */ 'components/instafeed'),
    ],
    'back-in-stock': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-back-in-stock" */ 'components/back-in-stock'),
    ],
    'klaviyo-popup': [
      immediateLoader,
      () => import(/* webpackChunkName: "component-klaviyo-popup" */ 'components/klaviyo-popup'),
    ],
    article: [
      intersectionLoader,
      () => import(/* webpackChunkName: "article-main" */ 'pages/article/main'),
    ],
    'metaobject-directory': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-metaobject-directory" */ 'components/metaobject-directory'
        ),
    ],
    'documentation-downloads': [
      immediateLoader,
      () =>
        import(
          /* webpackChunkName: "component-documentation-downloads" */ 'components/documentation-downloads'
        ),
    ],
    pagination: [
      immediateLoader,
      () => import(/* webpackChunkName: "component-pagination" */ 'components/pagination'),
    ],
    'password-overlay': [
      immediateLoader,
      () =>
        import(/* webpackChunkName: "component-password-overlay" */ 'components/password-overlay'),
    ],
    'region-switch': [
      [hasRegionMatch, onElementInteraction],
      () => import(/* webpackChunkName: "component-region-switch" */ 'components/region-switch'),
    ],
    'search-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-search-drawer" */ 'components/search-drawer'),
    ],
    'search-page-form': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-search-page-form" */ 'components/search-page-form'),
    ],
    filter: [
      immediateLoader,
      () => import(/* webpackChunkName: "component-filter" */ 'components/filter'),
    ],
    'placeholder-image': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-placeholder-image" */ 'components/placeholder-image'
        ),
    ],
    subdrawer: [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-subdrawer" */ 'components/subdrawer'),
    ],
    subcollections: [
      immediateLoader,
      () => import(/* webpackChunkName: "component-subcollections" */ 'components/subcollections'),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    'acoustic-comparison-audio': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-acoustic-comparison-audio" */ 'components/acoustic-comparison-audio'
        ),
    ],
    hotspots: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-hotspots-container" */ 'components/hotspots'),
    ],
    'shop-the-look-dialog': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-shop-the-look-dialog" */ 'components/shop-the-look-dialog'
        ),
    ],
    'featured-thumbnail-collection': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-featured-thumbnail-collection" */ 'components/featured-thumbnail-collection'
        ),
    ],
    overlay: [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-overlay" */ 'components/overlay'),
    ],
    'ai-disclaimer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-ai-disclaimer" */ 'components/ai-disclaimer'),
    ],
    'product-xr': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-xr" */ 'components/product-xr'),
    ],
    'product-ar-overlay': [
      hasUrlParam,
      () =>
        import(
          /* webpackChunkName: "component-product-ar-overlay" */ 'components/product-ar-overlay'
        ),
    ],
    'ugc-video-player': [
      onElementInteraction,
      () =>
        import(/* webpackChunkName: "component-ugc-video-player" */ 'components/ugc-video-player'),
    ],
    'grid-layout-switcher': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-grid-layout-switcher" */ 'components/grid-layout-switcher'
        ),
    ],
    'text-reveal': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-text-reveal" */ 'components/text-reveal'),
    ],
    'cursor-tooltip': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cursor-tooltip" */ 'components/cursor-tooltip'),
    ],

    cart: [intersectionLoader, () => import(/* webpackChunkName: "cart-main" */ 'pages/cart/main')],
    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-inventory': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-inventory" */ 'components/product-inventory'
        ),
    ],
    'product-recommendations': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "product-recommendations" */ 'pages/product/recommendations'),
    ],
    'product-linked': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-linked" */ 'pages/product/linked'),
    ],
    'product-item': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-item" */ 'components/product-item'),
    ],
    'product-sticky': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-sticky" */ 'pages/product/sticky'),
    ],
    'product-lightbox': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-lightbox" */ 'pages/product/lightbox'),
    ],
    'dimensions-image-lightbox': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-dimensions-image-lightbox" */ 'components/dimensions-image-lightbox'
        ),
    ],
  });
};
